// Responsive app chrome: top header + bottom nav on mobile, side nav on desktop.

import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  CalendarRange,
  CircleHelp,
  Home as HomeIcon,
  LogOut,
  Settings as SettingsIcon,
  Stethoscope,
} from "lucide-react";
import { api } from "../lib/api";
import { cn } from "../lib/cn";
import { Wordmark } from "./Brand";
import { Avatar } from "./ui/Avatar";

interface MeResponse {
  user: { id: string; email: string; name: string | null; timezone: string } | null;
  circles: Array<{
    id: string;
    name: string;
    role: "admin" | "member";
    loved_one_name: string | null;
  }>;
}

interface AppShellProps {
  children: ReactNode;
  activeTab?: "home" | "visits" | "calendar" | "notifications" | "settings" | "help";
}

export function AppShell({ children, activeTab = "home" }: AppShellProps) {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);

  useEffect(() => {
    api.get<MeResponse>("/api/me").then(setMe).catch(() => navigate("/signin", { replace: true }));
  }, [navigate]);

  const circle = me?.circles[0];
  const isAdmin = circle?.role === "admin";

  async function signOut() {
    await api.post("/api/auth/logout").catch(() => {});
    navigate("/signin", { replace: true });
  }

  return (
    <div className="min-h-full flex flex-col sm:flex-row">
      {/* Desktop side nav */}
      <aside className="hidden sm:flex sm:flex-col sm:w-64 sm:border-r sm:border-border sm:bg-surface">
        <div className="px-5 h-16 flex items-center border-b border-border">
          <Link to="/app" className="block">
            <Wordmark size="md" />
          </Link>
        </div>
        {circle && (
          <div className="px-5 py-4 border-b border-border">
            <div className="flex items-center gap-3">
              <Avatar name={circle.loved_one_name ?? circle.name} size="md" />
              <div className="min-w-0">
                <div className="font-semibold truncate text-sm">{circle.name}</div>
                {circle.loved_one_name && (
                  <div className="text-xs text-text-muted truncate">Caring for {circle.loved_one_name}</div>
                )}
              </div>
            </div>
          </div>
        )}
        <nav className="flex-1 p-3 space-y-1">
          <SideNavLink to="/app" label="Home" icon={<HomeIcon className="h-4 w-4" />} active={activeTab === "home"} />
          <SideNavLink
            to="/app/visits"
            label="Visits"
            icon={<Stethoscope className="h-4 w-4" />}
            active={activeTab === "visits"}
          />
          <SideNavLink
            to="/app/calendar"
            label="Calendar"
            icon={<CalendarRange className="h-4 w-4" />}
            active={activeTab === "calendar"}
          />
          <SideNavLink
            to="/app/notifications"
            label="Notifications"
            icon={<Bell className="h-4 w-4" />}
            active={activeTab === "notifications"}
          />
          {isAdmin && (
            <SideNavLink
              to="/app/settings"
              label="Settings"
              icon={<SettingsIcon className="h-4 w-4" />}
              active={activeTab === "settings"}
            />
          )}
          <SideNavLink
            to="/app/help"
            label="Help"
            icon={<CircleHelp className="h-4 w-4" />}
            active={activeTab === "help"}
          />
        </nav>
        <div className="p-3 border-t border-border text-sm">
          <div className="flex items-center gap-2 px-2 py-1.5 mb-2 rounded-md">
            <Avatar name={me?.user?.name ?? me?.user?.email} size="sm" />
            <div className="min-w-0">
              <div className="text-xs text-text-muted truncate">Signed in</div>
              <div className="text-xs font-medium truncate">{me?.user?.email}</div>
            </div>
          </div>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-text-muted hover:bg-surface-2 hover:text-text"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="sm:hidden border-b border-border bg-surface/85 backdrop-blur sticky top-0 z-20">
        <div className="px-5 h-14 flex items-center justify-between">
          <Link to="/app">
            <Wordmark size="sm" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/app/help" aria-label="Help" className="text-text-muted hover:text-text">
              <CircleHelp className="h-5 w-5" />
            </Link>
            <button
              onClick={signOut}
              aria-label="Sign out"
              className="flex items-center gap-1.5 text-sm text-text-muted hover:text-text"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
        {circle && (
          <div className="px-5 pb-3 flex items-center gap-2.5 text-sm">
            <Avatar name={circle.loved_one_name ?? circle.name} size="sm" />
            <div className="min-w-0">
              <div className="font-medium text-text truncate">{circle.name}</div>
              {circle.loved_one_name && (
                <div className="text-xs text-text-muted truncate">Caring for {circle.loved_one_name}</div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1 pb-24 sm:pb-6 fade-in">{children}</main>

      {/* Mobile bottom nav */}
      <nav
        className="sm:hidden fixed bottom-0 inset-x-0 z-20 bg-surface border-t border-border"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="grid grid-cols-4">
          <BottomNavItem
            to="/app"
            label="Home"
            icon={<HomeIcon className="h-5 w-5" />}
            active={activeTab === "home"}
          />
          <BottomNavItem
            to="/app/visits"
            label="Visits"
            icon={<Stethoscope className="h-5 w-5" />}
            active={activeTab === "visits"}
          />
          <BottomNavItem
            to="/app/calendar"
            label="Calendar"
            icon={<CalendarDays className="h-5 w-5" />}
            active={activeTab === "calendar"}
          />
          <BottomNavItem
            to="/app/notifications"
            label="Alerts"
            icon={<Bell className="h-5 w-5" />}
            active={activeTab === "notifications"}
          />
        </div>
      </nav>
    </div>
  );
}

function SideNavLink({
  to,
  label,
  icon,
  active,
}: {
  to: string;
  label: string;
  icon: ReactNode;
  active?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
        active
          ? "bg-primary-soft text-primary"
          : "text-text-muted hover:bg-surface-2 hover:text-text",
      )}
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

function BottomNavItem({
  to,
  label,
  icon,
  active,
  disabled,
}: {
  to: string;
  label: string;
  icon: ReactNode;
  active?: boolean;
  disabled?: boolean;
}) {
  const base =
    "flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[11px] font-medium";
  if (disabled) {
    return (
      <div className={cn(base, "text-text-faint")}>
        {icon}
        {label}
      </div>
    );
  }
  return (
    <Link
      to={to}
      className={cn(
        base,
        active ? "text-primary" : "text-text-muted hover:text-text",
      )}
    >
      {icon}
      {label}
    </Link>
  );
}
